import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert, Table, FloatingLabel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import AppNav from '../../header/AppNav';
import { CampServices } from '../../services/CampService';
import { TalukServices } from '../../services/Taluk';
import { PHCServices } from '../../services/Phcs';

const SecondaryScreeningReport = () => {
  const navigate = useNavigate();
  const [taluks, setTaluks] = useState([]);
  const [phcs, setPhcs] = useState([]);
  const [camps, setCamps] = useState([]);
  const [selectedTaluk, setSelectedTaluk] = useState('');
  const [selectedPhc, setSelectedPhc] = useState('');
  const [selectedCamp, setSelectedCamp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTaluks();
  }, []);

  useEffect(() => {
    if (selectedTaluk) {
      fetchPhcsByTaluk(selectedTaluk);
    } else {
      setPhcs([]);
      setSelectedPhc('');
      setCamps([]);
      setSelectedCamp('');
    }
  }, [selectedTaluk]);

  useEffect(() => {
    if (selectedPhc) {
      fetchCampsByPhc(selectedPhc);
    } else if (selectedTaluk && !selectedPhc) {
      // If taluk is selected but no PHC, show all camps for taluk
      setCamps([]);
      setSelectedCamp('');
    }
  }, [selectedPhc]);

  const fetchTaluks = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await TalukServices.getAllTaluks();
      let taluksData = [];
      if (Array.isArray(response)) {
        taluksData = response;
      } else if (response && Array.isArray(response.data)) {
        taluksData = response.data;
      }
      setTaluks(taluksData);
    } catch (error) {
      console.error('Error fetching taluks:', error);
      setError('Failed to load taluks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPhcsByTaluk = async (talukId) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await PHCServices.gePhcsByTalukId(talukId);
      let phcsData = [];
      if (Array.isArray(response)) {
        phcsData = response;
      } else if (response && Array.isArray(response.data)) {
        phcsData = response.data;
      }
      setPhcs(phcsData);
    } catch (error) {
      console.error('Error fetching PHCs:', error);
      setError('Failed to load PHCs. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCampsByPhc = async (phcId) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await CampServices.getAllCamps();
      let campsData = [];
      if (Array.isArray(response)) {
        campsData = response;
      } else if (response && Array.isArray(response.data)) {
        campsData = response.data;
      }
      // Filter camps by PHC
      const filteredCamps = campsData.filter(
        camp => camp.ashaBlockDetails?.phcDetails?.id === parseInt(phcId)
      );
      setCamps(filteredCamps);
    } catch (error) {
      console.error('Error fetching camps:', error);
      setError('Failed to load camps. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSecondaryScreeningReport = async () => {
    if (!selectedTaluk) {
      setError('Please select a taluk');
      return;
    }

    setIsDownloading(true);
    setError('');
    
    try {
      let response;
      
      // Determine which API to call based on selections
      if (selectedCamp) {
        response = await CampServices.getSecondaryScreeningComplaintsByCamp(selectedCamp);
      } else if (selectedPhc) {
        response = await CampServices.getSecondaryScreeningComplaintsByPhc(selectedPhc);
      } else {
        response = await CampServices.getSecondaryScreeningComplaintsByTaluk(selectedTaluk);
      }
      
      let data = [];
      if (Array.isArray(response)) {
        data = response;
      } else if (response && Array.isArray(response.data)) {
        data = response.data;
      }
      
      if (data.length === 0) {
        setError('No data found for the selected camp');
        setIsDownloading(false);
        return;
      }
      
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Helper function to format date as dd-mm-yyyy
      const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      // Helper function to get eye problems
      const getEyeProblems = (item) => {
        const problems = [];
        if (item.cataractType) problems.push("cataract");
        if (item.glaucomaType) problems.push("glaucoma");
        if(item.reType) problems.push("refractive error");
        if (item.drType) problems.push("diabetic retinopathy");
        if (item.ocDiagnosis) problems.push("other conditions");
        return problems.length > 0 ? problems.join(', ') : 'N/A';
      };

      // Sheet 1: Patient Details
      const patientDetailsData = data.map((item, index) => ({
        'Sl.No': index + 1,
        'Taluk': item.campDetails?.ashaBlockDetails?.phcDetails?.talukDetails?.taluk || 'N/A',
        'PHC': item.campDetails?.ashaBlockDetails?.phcDetails?.phc || 'N/A',
        'Asha Block': item.campDetails?.ashaBlockDetails?.ashaBlock || 'N/A',
        'Camp Location': item.campDetails?.campLocation || 'N/A',
        'Camp Date': formatDate(item.campDetails?.campDate),
        'Patient Name': item.patientDetails?.name || 'N/A',
        'Patient Age': item.patientDetails?.age || 'N/A',
        'Gender': item.patientDetails?.gender || 'N/A',
        'Mobile Number': item.patientDetails?.mobileNo || 'N/A',
        'Patient Address': item.patientDetails?.address || 'N/A',
        'ID Type': item.patientDetails?.idType || 'N/A',
        'ID Number': item.patientDetails?.idNumber || 'N/A',
        'Asha Name': item.patientDetails?.ashaDetails?.name || item.patientDetails?.ssAshaName || item.ssAshaName || 'N/A',
        'Asha Village': item.patientDetails?.ssAshaVillage || 'N/A',
        'Eye Problems': getEyeProblems(item)
      }));

      const ws1 = XLSX.utils.json_to_sheet(patientDetailsData);
      ws1['!cols'] = [
        { wpx: 60 },  // Sl.No
        { wpx: 150 }, // Taluk
        { wpx: 180 }, // PHC
        { wpx: 180 }, // Asha Block
        { wpx: 180 }, // Camp Location
        { wpx: 120 }, // Camp Date
        { wpx: 180 }, // Patient Name
        { wpx: 60 },  // Patient Age
        { wpx: 80 },  // Gender
        { wpx: 120 }, // Mobile Number
        { wpx: 250 }, // Patient Address
        { wpx: 100 }, // ID Type
        { wpx: 150 }, // ID Number
        { wpx: 180 }, // Asha Name
        { wpx: 180 }, // Asha Village
        { wpx: 250 }  // Eye Problems
      ];
      XLSX.utils.book_append_sheet(wb, ws1, 'Patient Details');

      // Sheet 2: Refractive
      const refractiveData = data
        .filter(item => item.reType != null && item.reType !== '')
        .map((item, index) => {
          // Concatenate Power To Power Right Eye fields
          const powerRightEyeSph = [
            item.powerToPowerRightEyeSphSymbol || '',
            item.powerToPowerRightEyeSph1 || '0',
            '.',
            item.powerToPowerRightEyeSph2 || '00'
          ].join('');

          const powerRightEyeCyl = [
            item.powerToPowerRightEyeCylSymbol || '',
            item.powerToPowerRightEyeCyl1 || '0',
            '.',
            item.powerToPowerRightEyeCyl2 || '00'
          ].join('');

          // Concatenate Power To Power Left Eye fields
          const powerLeftEyeCyl = [
            item.powerToPowerLeftEyeCylSymbol || '',
            item.powerToPowerLeftEyeCyl1 || '0',
            '.',
            item.powerToPowerLeftEyeCyl2 || '00'
          ].join('');

          const powerLeftEyeSph = [
            item.powerToPowerLeftEyeSphSymbol || '',
            item.powerToPowerLeftEyeSph1 || '0',
            '.',
            item.powerToPowerLeftEyeSph2 || '00'
          ].join('');

          return {
            'Sl.No': index + 1,
            'Taluk': item.campDetails?.ashaBlockDetails?.phcDetails?.talukDetails?.taluk || 'N/A',
            'PHC': item.campDetails?.ashaBlockDetails?.phcDetails?.phc || 'N/A',
            'Asha Block': item.campDetails?.ashaBlockDetails?.ashaBlock || 'N/A',
            'Camp Location': item.campDetails?.campLocation || 'N/A',
            'Camp Date': formatDate(item.campDetails?.campDate),
            'Patient Name': item.patientDetails?.name || 'N/A',
            'Patient Age': item.patientDetails?.age || 'N/A',
            'Gender': item.patientDetails?.gender || 'N/A',
            'Mobile Number': item.patientDetails?.mobileNo || 'N/A',
            'Patient Address': item.patientDetails?.address || 'N/A',
            'ID Type': item.patientDetails?.idType || 'N/A',
            'ID Number': item.patientDetails?.idNumber || 'N/A',
            'Asha Name': item.patientDetails?.ashaDetails?.name || item.patientDetails?.ssAshaName || item.ssAshaName || 'N/A',
            'Asha Village': item.patientDetails?.ssAshaVillage || 'N/A',
            'RE Type': item.reType || '',
            'Presbyopia Near Vision Sph': item.presbyopiaNearVisionSph || '',
            'Presbyopia Spectacle Distribution': item.presbyopiaSpectacleDistribution || '',
            'Presbyopia Spectacle Distribution Follow Up Date': formatDate(item.presbyopiaSpectacleDistributionFollowUpDate),
            'Power To Power Right Eye Sph': powerRightEyeSph,
            'Power To Power Left Eye Sph': powerLeftEyeSph,
            'Power To Power Right Eye Cyl': powerRightEyeCyl,
            'Power To Power Left Eye Cyl': powerLeftEyeCyl,
            'Power To Power Right Eye Axis': item.powerToPowerRightEyeAxis || '',
            'Power To Power Left Eye Axis': item.powerToPowerLeftEyeAxis || '',
            'Power To Power Right Eye VA': item.powerToPowerRightEyeVA || '',
            'Power To Power Left Eye VA': item.powerToPowerLeftEyeVA || '',
            'Power To Power Near Vision NV': item.powerToPowerNearVisionNV || '',
          };
        });

      const ws2 = XLSX.utils.json_to_sheet(refractiveData);
      ws2['!cols'] = [
        { wpx: 60 },  // Sl.No
        { wpx: 150 }, // Taluk
        { wpx: 180 }, // PHC
        { wpx: 180 }, // Asha Block
        { wpx: 180 }, // Camp Location
        { wpx: 120 }, // Camp Date
        { wpx: 180 }, // Patient Name
        { wpx: 60 },  // Patient Age
        { wpx: 80 },  // Gender
        { wpx: 120 }, // Mobile Number
        { wpx: 250 }, // Patient Address
        { wpx: 100 }, // ID Type
        { wpx: 150 }, // ID Number
        { wpx: 180 }, // Asha Name
        { wpx: 180 }, // Asha Village
        { wpx: 100 }, // RE Type
        { wpx: 180 }, // Presbyopia Near Vision Sph
        { wpx: 200 }, // Presbyopia Spectacle Distribution
        { wpx: 250 }, // Presbyopia Spectacle Distribution Follow Up Date
        { wpx: 180 }, // Power To Power Right Eye Sph
        { wpx: 180 }, // Power To Power Right Eye Cyl
        { wpx: 180 }, // Power To Power Right Eye Axis
        { wpx: 180 }, // Power To Power Right Eye VA
        { wpx: 180 }, // Power To Power Left Eye Sph
        { wpx: 180 }, // Power To Power Left Eye Cyl
        { wpx: 180 }, // Power To Power Left Eye Axis
        { wpx: 180 }, // Power To Power Left Eye VA
        { wpx: 180 }, // Power To Power Near Vision NV
      ];
      XLSX.utils.book_append_sheet(wb, ws2, 'Refractive');

      // Sheet 3: Cataract
      const cataractData = data
        .filter(item => item.cataractType != null && item.cataractType !== '')
        .map((item, index) => ({
          'Sl.No': index + 1,
          'Taluk': item.campDetails?.ashaBlockDetails?.phcDetails?.talukDetails?.taluk || 'N/A',
          'PHC': item.campDetails?.ashaBlockDetails?.phcDetails?.phc || 'N/A',
          'Asha Block': item.campDetails?.ashaBlockDetails?.ashaBlock || 'N/A',
          'Camp Location': item.campDetails?.campLocation || 'N/A',
          'Camp Date': formatDate(item.campDetails?.campDate),
          'Patient Name': item.patientDetails?.name || 'N/A',
          'Patient Age': item.patientDetails?.age || 'N/A',
          'Gender': item.patientDetails?.gender || 'N/A',
          'Mobile Number': item.patientDetails?.mobileNo || 'N/A',
          'Patient Address': item.patientDetails?.address || 'N/A',
          'ID Type': item.patientDetails?.idType || 'N/A',
          'ID Number': item.patientDetails?.idNumber || 'N/A',
          'Asha Name': item.patientDetails?.ashaDetails?.name || item.patientDetails?.ssAshaName || item.ssAshaName || 'N/A',
          'Asha Village': item.patientDetails?.ssAshaVillage || 'N/A',
          'Cataract Type': item.cataractType || '',
          'Cataract Eye': item.cataractEye || '',
          'Cataract Follow Up Date': formatDate(item.cataractFollowUpDate),
          'Cataract Referred Hospital': item.cataractReferredHospital?.hospitalName || ''
        }));

      const ws3 = XLSX.utils.json_to_sheet(cataractData);
      ws3['!cols'] = [
        { wpx: 60 },  // Sl.No
        { wpx: 150 }, // Taluk
        { wpx: 180 }, // PHC
        { wpx: 180 }, // Asha Block
        { wpx: 180 }, // Camp Location
        { wpx: 120 }, // Camp Date
        { wpx: 180 }, // Patient Name
        { wpx: 60 },  // Patient Age
        { wpx: 80 },  // Gender
        { wpx: 120 }, // Mobile Number
        { wpx: 250 }, // Patient Address
        { wpx: 100 }, // ID Type
        { wpx: 150 }, // ID Number
        { wpx: 180 }, // Asha Name
        { wpx: 180 }, // Asha Village
        { wpx: 150 }, // Cataract Type
        { wpx: 120 }, // Cataract Eye
        { wpx: 150 }, // Cataract Follow Up Date
        { wpx: 180 }  // Cataract Referred Hospital
      ];
      XLSX.utils.book_append_sheet(wb, ws3, 'Cataract');

      // Sheet 4: Glaucoma
      const glaucomaData = data
        .filter(item => item.glaucomaType != null && item.glaucomaType !== '')
        .map((item, index) => ({
          'Sl.No': index + 1,
          'Taluk': item.campDetails?.ashaBlockDetails?.phcDetails?.talukDetails?.taluk || 'N/A',
          'PHC': item.campDetails?.ashaBlockDetails?.phcDetails?.phc || 'N/A',
          'Asha Block': item.campDetails?.ashaBlockDetails?.ashaBlock || 'N/A',
          'Camp Location': item.campDetails?.campLocation || 'N/A',
          'Camp Date': formatDate(item.campDetails?.campDate),
          'Patient Name': item.patientDetails?.name || 'N/A',
          'Patient Age': item.patientDetails?.age || 'N/A',
          'Gender': item.patientDetails?.gender || 'N/A',
          'Mobile Number': item.patientDetails?.mobileNo || 'N/A',
          'Patient Address': item.patientDetails?.address || 'N/A',
          'ID Type': item.patientDetails?.idType || 'N/A',
          'ID Number': item.patientDetails?.idNumber || 'N/A',
          'Asha Name': item.patientDetails?.ashaDetails?.name || item.patientDetails?.ssAshaName || item.ssAshaName || 'N/A',
          'Asha Village': item.patientDetails?.ssAshaVillage || 'N/A',
          'Glaucoma Type': item.glaucomaType || '',
          'IOP Left Eye': item.glaucomaIOPLeftEye || '',
          'IOP Right Eye': item.glaucomaIOPRightEye || '',
          'Prescribed Medication': item.glaucomaPrescribedMedication || '',
          'Other Prescribed Medication': item.glaucomaOtherPrescribedMedication || '',
          'Diagnosed Follow Up Date': formatDate(item.glaucomaDiagnosedFollowUpDate),
          'Suspected Follow Up Date': formatDate(item.glaucomaSuspectedFollowUpDate),
          'Referred Hospital': item.glaucomaReferredHospital?.hospitalName || '',
          'Follow Up Place': item.glaucomaPlaceOfFollowUp?.name || ''
        }));

      const ws4 = XLSX.utils.json_to_sheet(glaucomaData);
      ws4['!cols'] = [
        { wpx: 60 },  // Sl.No
        { wpx: 150 }, // Taluk
        { wpx: 180 }, // PHC
        { wpx: 180 }, // Asha Block
        { wpx: 180 }, // Camp Location
        { wpx: 120 }, // Camp Date
        { wpx: 180 }, // Patient Name
        { wpx: 60 },  // Patient Age
        { wpx: 80 },  // Gender
        { wpx: 120 }, // Mobile Number
        { wpx: 250 }, // Patient Address
        { wpx: 100 }, // ID Type
        { wpx: 150 }, // ID Number
        { wpx: 180 }, // Asha Name
        { wpx: 180 }, // Asha Village
        { wpx: 150 }, // Glaucoma Type
        { wpx: 100 }, // IOP Left Eye
        { wpx: 100 }, // IOP Right Eye
        { wpx: 180 }, // Prescribed Medication
        { wpx: 220 }, // Other Prescribed Medication
        { wpx: 180 }, // Diagnosed Follow Up Date
        { wpx: 180 },  // Suspected Follow Up Date
        { wpx: 180 }  // Follow Up Place
      ];
      XLSX.utils.book_append_sheet(wb, ws4, 'Glaucoma');

      // Sheet 5: DR (Diabetic Retinopathy)
      const drData = data
        .filter(item => item.drType != null && item.drType !== '')
        .map((item, index) => ({
          'Sl.No': index + 1,
          'Taluk': item.campDetails?.ashaBlockDetails?.phcDetails?.talukDetails?.taluk || 'N/A',
          'PHC': item.campDetails?.ashaBlockDetails?.phcDetails?.phc || 'N/A',
          'Asha Block': item.campDetails?.ashaBlockDetails?.ashaBlock || 'N/A',
          'Camp Location': item.campDetails?.campLocation || 'N/A',
          'Camp Date': formatDate(item.campDetails?.campDate),
          'Patient Name': item.patientDetails?.name || 'N/A',
          'Patient Age': item.patientDetails?.age || 'N/A',
          'Gender': item.patientDetails?.gender || 'N/A',
          'Mobile Number': item.patientDetails?.mobileNo || 'N/A',
          'Patient Address': item.patientDetails?.address || 'N/A',
          'ID Type': item.patientDetails?.idType || 'N/A',
          'ID Number': item.patientDetails?.idNumber || 'N/A',
          'Asha Name': item.patientDetails?.ashaDetails?.name || item.patientDetails?.ssAshaName || item.ssAshaName || 'N/A',
          'Asha Village': item.patientDetails?.ssAshaVillage || 'N/A',
          'Diabetic Retinopathy Type': item.drType || '',
          'Diabetic Retinopathy Follow Up Date': formatDate(item.drFollowUpDate),
          'Diabetic Retinopathy Referred Hospital': item.drReferredHospital?.hospitalName || ''
          
        }));

      const ws5 = XLSX.utils.json_to_sheet(drData);
      ws5['!cols'] = [
        { wpx: 60 },  // Sl.No
        { wpx: 150 }, // Taluk
        { wpx: 180 }, // PHC
        { wpx: 180 }, // Asha Block
        { wpx: 180 }, // Camp Location
        { wpx: 120 }, // Camp Date
        { wpx: 180 }, // Patient Name
        { wpx: 60 },  // Patient Age
        { wpx: 80 },  // Gender
        { wpx: 120 }, // Mobile Number
        { wpx: 250 }, // Patient Address
        { wpx: 100 }, // ID Type
        { wpx: 150 }, // ID Number
        { wpx: 180 }, // Asha Name
        { wpx: 180 }, // Asha Village
        { wpx: 150 }, // Diabetic Retinopathy Type
        { wpx: 150 }, // Diabetic Retinopathy Follow Up Date
        { wpx: 180 }, // Follow Up Place
        { wpx: 180 }  // Diabetic Retinopathy Referred Hospital
        
      ];
      XLSX.utils.book_append_sheet(wb, ws5, 'DR');

      // Sheet 6: Others
      const othersData = data
        .filter(item => item.ocDiagnosis != null && item.ocDiagnosis !== '')
        .map((item, index) => ({
          'Sl.No': index + 1,
          'Taluk': item.campDetails?.ashaBlockDetails?.phcDetails?.talukDetails?.taluk || 'N/A',
          'PHC': item.campDetails?.ashaBlockDetails?.phcDetails?.phc || 'N/A',
          'Asha Block': item.campDetails?.ashaBlockDetails?.ashaBlock || 'N/A',
          'Camp Location': item.campDetails?.campLocation || 'N/A',
          'Camp Date': formatDate(item.campDetails?.campDate),
          'Patient Name': item.patientDetails?.name || 'N/A',
          'Patient Age': item.patientDetails?.age || 'N/A',
          'Gender': item.patientDetails?.gender || 'N/A',
          'Mobile Number': item.patientDetails?.mobileNo || 'N/A',
          'Patient Address': item.patientDetails?.address || 'N/A',
          'ID Type': item.patientDetails?.idType || 'N/A',
          'ID Number': item.patientDetails?.idNumber || 'N/A',
          'Asha Name': item.patientDetails?.ashaDetails?.name || item.patientDetails?.ssAshaName || item.ssAshaName || 'N/A',
          'Asha Village': item.patientDetails?.ssAshaVillage || 'N/A',
          'Other Condition Diagnosis': item.ocDiagnosis || '',
          'Other Condition Prescribed Medication': item.ocPrescribedMedication || '',
          'Other Condition Place Of Follow Up': item.ocPlaceOfFollowUp?.name || ''
        }));

      const ws6 = XLSX.utils.json_to_sheet(othersData);
      ws6['!cols'] = [
        { wpx: 60 },  // Sl.No
        { wpx: 150 }, // Taluk
        { wpx: 180 }, // PHC
        { wpx: 180 }, // Asha Block
        { wpx: 180 }, // Camp Location
        { wpx: 120 }, // Camp Date
        { wpx: 180 }, // Patient Name
        { wpx: 60 },  // Patient Age
        { wpx: 80 },  // Gender
        { wpx: 120 }, // Mobile Number
        { wpx: 250 }, // Patient Address
        { wpx: 100 }, // ID Type
        { wpx: 150 }, // ID Number
        { wpx: 180 }, // Asha Name
        { wpx: 180 }, // Asha Village
        { wpx: 220 }, // OC Diagnosis
        { wpx: 220 }, // OC Prescribed Medication
        { wpx: 180 }  // OC Place Of Follow Up
      ];
      XLSX.utils.book_append_sheet(wb, ws6, 'Others');

      // Generate filename based on selection
      let fileName = 'Secondary_Screening_Report_';
      if (selectedCamp) {
        const campName = camps.find(c => c.id === parseInt(selectedCamp))?.campLocation || 'Camp';
        fileName += `${campName.replace(/\s+/g, '_')}_`;
      } else if (selectedPhc) {
        const phcName = phcs.find(p => p.id === parseInt(selectedPhc))?.phc || 'PHC';
        fileName += `PHC_${phcName.replace(/\s+/g, '_')}_`;
      } else {
        const talukName = taluks.find(t => t.id === parseInt(selectedTaluk))?.taluk || 'Taluk';
        fileName += `Taluk_${talukName.replace(/\s+/g, '_')}_`;
      }
      fileName += `${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download file
      XLSX.writeFile(wb, fileName);
      
      setError('');
    } catch (error) {
      console.error('Error downloading report:', error);
      setError('Failed to download report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div>
      <AppNav />
      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <Button
              variant="outline-primary"
              onClick={() => navigate('/reports')}
              className="mb-3"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Reports
            </Button>
            <h2>Secondary Screening Report</h2>
            <p className="text-muted">Download detailed secondary screening reports by camp</p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Row>
          <Col md={3}>
            <FloatingLabel controlId="taluk" label="Select Taluk *" className="mb-3">
              <Form.Select
                value={selectedTaluk}
                onChange={(e) => {
                  setSelectedTaluk(e.target.value);
                  setSelectedPhc('');
                  setSelectedCamp('');
                  setError('');
                }}
                disabled={isLoading}
                style={{ height: 'calc(3.5rem + 2px)' }}
              >
                <option value="">-- Select a Taluk --</option>
                {taluks.map((taluk) => (
                  <option key={taluk.id} value={taluk.id}>
                    {taluk.taluk}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            </Col>
            <Col md={3}>
            <FloatingLabel controlId="phc" label="Select PHC (Optional)" className="mb-3">
              <Form.Select
                value={selectedPhc}
                onChange={(e) => {
                  setSelectedPhc(e.target.value);
                  setSelectedCamp('');
                  setError('');
                }}
                disabled={isLoading || !selectedTaluk}
                style={{ height: 'calc(3.5rem + 2px)' }}
              >
                <option value="">-- All PHCs --</option>
                {phcs.map((phc) => (
                  <option key={phc.id} value={phc.id}>
                    {phc.phc}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            </Col>
            <Col md={6}>
            <FloatingLabel controlId="camp" label="Select Camp (Optional)" className="mb-3">
              <Form.Select
                value={selectedCamp}
                onChange={(e) => {
                  setSelectedCamp(e.target.value);
                  setError('');
                }}
                disabled={isLoading || !selectedPhc}
                style={{ height: 'calc(3.5rem + 2px)' }}
              >
                <option value="">-- All Camps --</option>
                {camps.map((camp) => (
                  <option key={camp.id} value={camp.id}>
                    {camp.campLocation} - {new Date(camp.campDate).toLocaleDateString('en-GB')}
                  </option>
                ))}
              </Form.Select>
            </FloatingLabel>
            </Col>
            </Row>
            <Row>
            <Col md={{offset: 2, span: 4}}>
            <Button
              variant="success"
              onClick={downloadSecondaryScreeningReport}
              disabled={!selectedTaluk || isDownloading}
              className="w-100"
              style={{ height: 'calc(3.5rem + 2px)' }}
            >
              {isDownloading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Downloading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Download Excel Report
                </>
              )}
            </Button>
          </Col>

        </Row>

        {isLoading && (
          <div className="text-center mt-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading camps...</span>
            </Spinner>
          </div>
        )}
      </Container>
    </div>
  );
};

export default SecondaryScreeningReport;
